/**
 * Persist the last Reporting left-pane item (get started / new report / inbox id).
 * Scoped per hotel + signed-in staff — same keying as the composer draft.
 */

import { reportingComposerUserKey } from './reportingComposerStorage';

const STORAGE_PREFIX = 'lodgefirst_reporting_nav_v1';

export const REPORTING_NAV_START = 'start';
export const REPORTING_NAV_NEW = 'new';

/** URL segment under /reporting-analytics/reporting — start | new | inbox id. */
export type ReportingNavItemId = typeof REPORTING_NAV_START | typeof REPORTING_NAV_NEW | string;

const storageKey = (): string | null => {
  const userKey = reportingComposerUserKey();
  if (!userKey) return null;
  const hotelId = (typeof localStorage !== 'undefined'
    && localStorage.getItem('hotel_data_id'))
    || 'default';
  return `${STORAGE_PREFIX}:${hotelId}:${userKey}`;
};

export const sanitizeReportingNavItem = (raw: unknown): ReportingNavItemId => {
  const value = String(raw || '').trim();
  if (!value) return REPORTING_NAV_START;
  if (value === REPORTING_NAV_START || value === REPORTING_NAV_NEW) return value;
  // Inbox ids are numeric strings from manager_reports_id_seq.
  if (/^[A-Za-z0-9_-]{1,80}$/.test(value)) return value;
  return REPORTING_NAV_START;
};

export const loadReportingNavItem = (): ReportingNavItemId => {
  try {
    if (typeof localStorage === 'undefined') return REPORTING_NAV_START;
    const key = storageKey();
    if (!key) return REPORTING_NAV_START;
    return sanitizeReportingNavItem(localStorage.getItem(key));
  } catch {
    return REPORTING_NAV_START;
  }
};

export const saveReportingNavItem = (navItem: ReportingNavItemId): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = storageKey();
    if (!key) return;
    localStorage.setItem(key, sanitizeReportingNavItem(navItem));
  } catch {
    // Quota / private mode — in-memory nav still works until refresh.
  }
};

export const isReportingInboxNavItem = (navItem: ReportingNavItemId): boolean => (
  navItem !== REPORTING_NAV_START
  && navItem !== REPORTING_NAV_NEW
  && !isReportingPeekNavItem(navItem)
);

const PEEK_NAV_RE = /^peek-([A-Za-z0-9_-]{1,40})$/;

/** Temporary tab to read a lower-permission staff report before closing it. */
export const isReportingPeekNavItem = (navItem: ReportingNavItemId): boolean => (
  PEEK_NAV_RE.test(String(navItem || ''))
);

export const reportingPeekNavItem = (reportId: string): string => (
  `peek-${String(reportId || '').trim()}`
);

export const reportingPeekIdFromNav = (navItem: ReportingNavItemId): string => {
  const match = String(navItem || '').match(PEEK_NAV_RE);
  return match ? match[1] : '';
};

/** How long a filed or dismissed item stays before it is dropped. */
export const REVIEWED_REPORT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Remaining time until a filed or dismissed item is dropped (7 days after mark). */
export const reviewedReportDeleteHint = (reviewedAt: string | null | undefined): string => {
  if (!reviewedAt) return '';
  const end = new Date(reviewedAt).getTime() + REVIEWED_REPORT_TTL_MS;
  const ms = end - Date.now();
  if (!Number.isFinite(end) || ms <= 0) return 'Deleting soon';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (days >= 1) return `Deletes in ${days}d`;
  if (hours >= 1) return `Deletes in ${hours}h`;
  return `Deletes in ${Math.max(1, mins)}m`;
};
